import classnames from "classnames";
import { parse } from "papaparse";
import React, { useState } from "react";
import { Form } from "react-bootstrap";

import type { IOption } from "../../../../components/StyledSelect/StyledSelect";
import { StyledSelect as Select } from "../../../../components/StyledSelect/StyledSelect";
import type { ICacheItemRequest } from "../../../../hooks/reactQueryHooks/useCacheItems";
interface IProps {
  maxFileSize?: number;
  maxStrokeLength?: number;
  classNames?: Record<string, boolean>;
  onFileChange: (data: ICacheItemRequest[]) => void;
}

const checkIfCSVValid = (
  data: Array<string[]>,
  maxStrokeLength: number,
): string | null => {
  if (!data.length) {
    return "CSV file is empty";
  }

  if (data.length > maxStrokeLength) {
    return `CSV file must have less then ${maxStrokeLength} rows of data`;
  }

  return null;
};

const CSVFileInput: React.FC<IProps> = ({
  maxFileSize = 1677721,
  maxStrokeLength = 1000,
  classNames,
  onFileChange,
}) => {
  const [errorText, setErrorText] = useState<string>("");
  const [columns, setColumns] = useState<number[]>([]);
  const [options, setOptions] = useState<IOption[]>([]);
  const [queries, setQueries] = useState<string[][]>([]);

  const parseFileCallback = (data: Papa.ParseResult<string[]>) => {
    const { data: dataList, errors: dataErrors } = data;
    const error = checkIfCSVValid(dataList, maxStrokeLength);
    if (error) {
      handleError(error);
      return;
    }

    const filteredData = dataList.filter(
      (item: string[]) => item[0] && item[1],
    );

    setQueries(filteredData);

    const promptIndex = dataList[0].findIndex(
      (item: string) => item.toLowerCase().trim() === "prompt",
    );
    const answerIndex = dataList[0].findIndex(
      (item: string) => item.toLowerCase().trim() === "answer",
    );

    const parsedData = filteredData.map((item: string[]) => ({
      prompt: item[promptIndex < 0 ? 0 : promptIndex],
      answer: item[answerIndex < 0 ? 1 : answerIndex],
    }));

    setOptions(
      dataList[0].map(item => ({
        value: item,
        label: item || <i>No value provided</i>,
      })),
    );
    setColumns([
      promptIndex < 0 ? 0 : promptIndex,
      answerIndex < 0 ? 1 : answerIndex,
    ]);

    onFileChange(parsedData);
    setErrorText("");

    if (dataErrors.length) {
      console.error(dataErrors);
    }
  };

  const handleFileChange = (e: any) => {
    const file = e?.target?.files[0];

    if (!file) {
      handleError("");
      return;
    }

    if (file.size > maxFileSize) {
      handleError(`File size must be less than ${maxFileSize}b`);
      return;
    }

    parse(file, {
      delimiter: "",
      skipEmptyLines: true,
      delimitersToGuess: [";", ","],
      complete: parseFileCallback,
    });
  };

  const handleError = (error: string) => {
    onFileChange([]);
    setErrorText(error);
  };

  const handleChangeColumn = (option: IOption, columnIndex: number) => {
    const newIndex = options.findIndex(
      (item: IOption) => item.value === option.value,
    );
    const newColumns = columns.map((item, index) =>
      columnIndex === index ? newIndex : item,
    );
    setColumns(newColumns);

    const parsedData = queries.map((query: string[]) => ({
      prompt: query[newColumns[0]],
      answer: query[newColumns[1]],
    }));

    onFileChange(parsedData);
  };

  return (
    <>
      <label className="d-block fs-7 form-label">CSV (FAQ) file</label>
      <Form.Control
        className={classnames("csv-file-input", classNames?.["csv-file-input"])}
        type="file"
        accept=".csv"
        onChange={handleFileChange}
      />
      {!!errorText && (
        <p className="file-alert alert-danger mt-3 p-3 rounded-3" role="alert">
          {errorText}
        </p>
      )}
      <p className="short-explanation">
        Each CSV line should have the prompt and answer
      </p>
      {!!options.length && (
        <>
          <div className="add-cache-select-wrapper mb-7">
            <label className="d-block mt-2 fs-7 required form-label">
              Prompt column
            </label>
            <Select
              isDisabled={false}
              isLoading={false}
              isSearchable={false}
              options={options}
              onChange={option => handleChangeColumn(option, 0)}
              value={options[columns[0]]}
              placeholder="Select an Prompt"
            />
          </div>
          <div className="mb-7">
            <label className="d-block mt-2 fs-7 required form-label">
              Answer column
            </label>
            <Select
              isDisabled={false}
              isLoading={false}
              isSearchable={false}
              options={options}
              onChange={option => handleChangeColumn(option, 1)}
              value={options[columns[1]]}
              placeholder="Select an Answer"
            />
          </div>
        </>
      )}
    </>
  );
};

export { CSVFileInput };
